import { useEffect, useLayoutEffect, useRef } from 'react';

/*
 * A scroll wheel for picking one option, like the clock app's timer (Saif, 3 Nov 2026): five rows visible, the middle one is
 * the choice, scroll-snap holds it there. Scrolling, a tap on a row and the arrow keys all move it; `onChange` fires once the
 * wheel settles. The selected option is always scrolled into the middle when it changes from outside (a day switch).
 */
export interface WheelOption { key: string; label: string; tag?: string }

const ROW = 44;
const PAD = 2 * ROW; // two rows above and below the middle one

export function TimeWheel({ options, value, onChange, label }: { options: WheelOption[]; value: string; onChange: (key: string) => void; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const latest = useRef({ options, value, onChange });
  latest.current = { options, value, onChange };
  const idx = Math.max(0, options.findIndex((o) => o.key === value));

  // Put the chosen row in the middle whenever the choice (or the list) changes without the wheel being scrolled there already.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (Math.round(el.scrollTop / ROW) !== idx) el.scrollTo({ top: idx * ROW });
  }, [idx, options]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const settle = () => {
      const { options: opts, value: v, onChange: change } = latest.current;
      const k = Math.min(opts.length - 1, Math.max(0, Math.round(el.scrollTop / ROW)));
      const o = opts[k];
      if (o && o.key !== v) change(o.key);
    };
    const onScroll = () => {
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(settle, 90);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('scrollend', settle);
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('scrollend', settle);
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  const go = (k: number) => {
    const el = ref.current;
    if (!el) return;
    const i = Math.min(options.length - 1, Math.max(0, k));
    el.scrollTo({ top: i * ROW, behavior: 'smooth' });
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); go(idx + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); go(idx - 1); }
    else if (e.key === 'Home') { e.preventDefault(); go(0); }
    else if (e.key === 'End') { e.preventDefault(); go(options.length - 1); }
  };

  return (
    <div className="timewheelwrap">
      <div className="twband" aria-hidden="true" />
      <div className="timewheel" ref={ref} role="listbox" aria-label={label} tabIndex={0} aria-activedescendant={options[idx] ? `wheel-${options[idx].key}` : undefined} onKeyDown={onKey}>
        <div className="twpad" style={{ height: PAD }} aria-hidden="true" />
        {options.map((o, i) => (
          <div key={o.key} id={`wheel-${o.key}`} className="twrow" role="option" aria-selected={i === idx} onClick={() => go(i)}>
            <span>{o.label}</span>
            {o.tag && <span className="tag">{o.tag}</span>}
          </div>
        ))}
        <div className="twpad" style={{ height: PAD }} aria-hidden="true" />
      </div>
    </div>
  );
}
